import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { useCRM } from '../context/CRMContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const TEST_TYPES = ['SAT', 'ACT', 'GRE', 'GMAT', 'TOEFL', 'IELTS', 'PTE', 'DET'];

const TEST_CFG = {
  SAT:   { color:'#2563eb', bg:'#eff6ff', badge:'#dbeafe', sections:['Reading & Writing','Math'],                        maxSec:[800,800],        maxTotal:1600, label:'SAT',   note:'Scale 400–1600',       dualTeacher:true,  t1Label:'Math Teacher',  t2Label:'Verbal Teacher' },
  ACT:   { color:'#7c3aed', bg:'#f5f3ff', badge:'#ede9fe', sections:['English','Math','Reading','Science'],              maxSec:[36,36,36,36],    maxTotal:36,   label:'ACT',   note:'Composite 1–36',       dualTeacher:true,  t1Label:'Math Teacher',  t2Label:'English / Reading Teacher', composite:true },
  GRE:   { color:'#0891b2', bg:'#ecfeff', badge:'#cffafe', sections:['Verbal','Quantitative','Analytical Writing'],      maxSec:[170,170,6],      maxTotal:340,  label:'GRE',   note:'V+Q 260–340, AW 0–6',  dualTeacher:true,  t1Label:'Quant Teacher', t2Label:'Verbal Teacher', excludeFromTotal:['Analytical Writing'] },
  GMAT:  { color:'#059669', bg:'#ecfdf5', badge:'#d1fae5', sections:['Verbal','Quantitative','Integrated Reasoning','Analytical Writing'], maxSec:[60,60,8,6], maxTotal:800, label:'GMAT', note:'Total 200–800', dualTeacher:true,  t1Label:'Quant Teacher', t2Label:'Verbal Teacher' },
  TOEFL: { color:'#dc2626', bg:'#fef2f2', badge:'#fee2e2', sections:['Reading','Listening','Speaking','Writing'],        maxSec:[30,30,30,30],    maxTotal:120,  label:'TOEFL', note:'iBT 0–120' },
  IELTS: { color:'#d97706', bg:'#fffbeb', badge:'#fef3c7', sections:['Listening','Reading','Writing','Speaking'],        maxSec:[9,9,9,9],        maxTotal:9,    label:'IELTS', note:'Overall Band 0–9',      isBand:true },
  PTE:   { color:'#be185d', bg:'#fdf2f8', badge:'#fce7f3', sections:['Speaking & Writing','Reading','Listening'],        maxSec:[90,90,90],       maxTotal:90,   label:'PTE',   note:'Academic 10–90' },
  DET:   { color:'#374151', bg:'#f9fafb', badge:'#f3f4f6', sections:['Literacy','Comprehension','Production','Conversation'], maxSec:[160,160,160,160], maxTotal:160, label:'DET', note:'Duolingo 10–160' },
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Format a list of day indices like [1,3,5] → "Mon, Wed, Fri"
const fmtDays = (days = []) => days.map(d => DAY_LABELS[d]).join(', ') || 'No days set';

// Format HH:MM → "4:00 PM"
const fmtTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
};

const CLASS_COLORS = ['#7c3aed','#2563eb','#059669','#dc2626','#d97706','#be185d','#0891b2','#374151'];

// Test Series event types
const SERIES_TYPES = {
  diagnostic: { label:'Diagnostic',   color:'#2563eb', bg:'#eff6ff', badge:'#dbeafe', icon:'🔬', desc:'Baseline assessment' },
  practice:   { label:'Practice',     color:'#7c3aed', bg:'#f5f3ff', badge:'#ede9fe', icon:'📝', desc:'Untimed practice with review' },
  mock:       { label:'Mock Test',    color:'#d97706', bg:'#fffbeb', badge:'#fef3c7', icon:'🎯', desc:'Timed full simulation' },
  full:       { label:'Full Length',  color:'#dc2626', bg:'#fef2f2', badge:'#fee2e2', icon:'⚡', desc:'Exam-condition full test' },
};

// Days until a date string (negative = past)
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + 'T00:00:00').getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000);
};

const fmtShortDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
};

const TABS = [
  { id:'dashboard',     label:'Dashboard' },
  { id:'students',      label:'Students' },
  { id:'classes',       label:'Classes' },
  { id:'schedule',      label:'Schedule' },
  { id:'scores',        label:'Scores' },
  { id:'notifications', label:'Notifications' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getDisplayName = (l) =>
  (l.firstName || l.lastName)
    ? [l.firstName, l.lastName].filter(Boolean).join(' ')
    : (l.name || '');

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
};

const today = () => new Date().toISOString().split('T')[0];

const calcTotal = (sections, testType) => {
  const cfg = TEST_CFG[testType];
  if (!cfg) return null;
  const vals = cfg.sections.map(s => sections?.[s]);
  const filled = vals.filter(v => v !== undefined && v !== '' && v !== null);
  if (!filled.length) return null;
  if (cfg.composite) {
    return parseFloat((filled.reduce((a, b) => a + parseFloat(b || 0), 0) / filled.length).toFixed(1));
  }
  if (cfg.isBand) {
    return parseFloat((filled.reduce((a, b) => a + parseFloat(b || 0), 0) / filled.length).toFixed(1));
  }
  if (cfg.excludeFromTotal) {
    let sum = 0;
    cfg.sections.forEach(s => {
      if (!cfg.excludeFromTotal.includes(s) && sections?.[s] !== undefined && sections[s] !== '') {
        sum += parseFloat(sections[s] || 0);
      }
    });
    return sum || null;
  }
  return parseFloat(filled.reduce((a, b) => a + parseFloat(b || 0), 0).toFixed(1));
};

const getScorePercent = (total, testType) => {
  const cfg = TEST_CFG[testType];
  if (!cfg || total == null) return 0;
  return Math.min(100, Math.round((total / cfg.maxTotal) * 100));
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function ScoreSparkline({ scores, testType }) {
  const pts = useMemo(() => {
    return [...scores]
      .sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const db2 = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return da - db2;
      })
      .map(s => calcTotal(s.sections, testType))
      .filter(v => v !== null);
  }, [scores, testType]);

  if (pts.length < 2) return null;
  const cfg = TEST_CFG[testType] || {};
  const maxV = cfg.maxTotal || 100;
  const W = 120, H = 36, P = 4;
  const xs = pts.map((_, i) => P + (i / (pts.length - 1)) * (W - P * 2));
  const ys = pts.map(v => H - P - (v / maxV) * (H - P * 2));
  const color = cfg.color || '#7c3aed';
  return (
    <svg width={W} height={H} style={{ display:'block', marginTop:8 }}>
      <polyline
        points={xs.map((x, i) => `${x},${ys[i]}`).join(' ')}
        fill="none" stroke={color} strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round"
      />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="3" fill={color} />)}
    </svg>
  );
}

function ScoreBar({ value, max, color }) {
  const pct = max ? Math.min(100, Math.round(((value || 0) / max) * 100)) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:5, background:'rgba(0,0,0,0.07)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:3 }} />
      </div>
      <span style={{ fontSize:12, color:'#64748b', minWidth:28, textAlign:'right' }}>{value ?? '–'}</span>
    </div>
  );
}

function StatCard({ label, value, color = '#7c3aed', icon }) {
  return (
    <div style={{ background:'#fff', borderRadius:14, padding:'18px 20px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)', display:'flex', gap:14, alignItems:'center' }}>
      <div style={{ width:44, height:44, borderRadius:12, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:24, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>{label}</div>
      </div>
    </div>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────
function AddClassModal({ open, onClose, onSave, teachers, editData }) {
  const blank = {
    name:'', testType:'SAT',
    teacher1Id:'', teacher1Name:'',
    teacher2Id:'', teacher2Name:'',
    // Dual schedule (SAT/ACT/GRE/GMAT) — separate math vs verbal/English days+times
    mathDays:[], mathStartTime:'', mathEndTime:'',
    verbalDays:[], verbalStartTime:'', verbalEndTime:'',
    // Single schedule (TOEFL/IELTS/PTE/DET)
    scheduleDays:[], startTime:'', endTime:'',
    startDate:today(), maxStudents:15, description:'', status:'active',
  };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (editData) {
      setForm({
        name:editData.name||'', testType:editData.testType||'SAT',
        teacher1Id:editData.teacher1Id||editData.teacherId||'',
        teacher1Name:editData.teacher1Name||editData.teacherName||'',
        teacher2Id:editData.teacher2Id||'', teacher2Name:editData.teacher2Name||'',
        mathDays:editData.mathDays||[], mathStartTime:editData.mathStartTime||'', mathEndTime:editData.mathEndTime||'',
        verbalDays:editData.verbalDays||[], verbalStartTime:editData.verbalStartTime||'', verbalEndTime:editData.verbalEndTime||'',
        scheduleDays:editData.scheduleDays||[], startTime:editData.startTime||'',
        endTime:editData.endTime||'', startDate:editData.startDate||today(),
        maxStudents:editData.maxStudents||15, description:editData.description||'',
        status:editData.status||'active',
      });
    } else { setForm(blank); }
  }, [open]); // eslint-disable-line

  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));
  const cfg = TEST_CFG[form.testType] || {};

  const handleT1 = e => { const t = teachers.find(u => u.id === e.target.value); set('teacher1Id', e.target.value); set('teacher1Name', t?.name || ''); };
  const handleT2 = e => { const t = teachers.find(u => u.id === e.target.value); set('teacher2Id', e.target.value); set('teacher2Name', t?.name || ''); };
  const toggleDay    = (d) => setForm(p => ({ ...p, scheduleDays: p.scheduleDays.includes(d) ? p.scheduleDays.filter(x => x !== d) : [...p.scheduleDays, d].sort() }));
  const toggleMathDay  = (d) => setForm(p => ({ ...p, mathDays:   p.mathDays.includes(d)   ? p.mathDays.filter(x => x !== d)   : [...p.mathDays,   d].sort() }));
  const toggleVerbalDay = (d) => setForm(p => ({ ...p, verbalDays: p.verbalDays.includes(d) ? p.verbalDays.filter(x => x !== d) : [...p.verbalDays, d].sort() }));

  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  const lbl = { fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16, overflowY:'auto' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:540, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', margin:'auto' }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:20, color:'#0f172a' }}>{editData ? 'Edit Class' : 'New Class'}</div>
        <div style={{ display:'grid', gap:14 }}>

          {/* Name + Test Type */}
          <div>
            <label style={lbl}>Class Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. SAT Batch A – Morning" style={inp} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={lbl}>Test Type *</label>
              <select value={form.testType} onChange={e => set('testType', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
                {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Max Students</label>
              <input type="number" value={form.maxStudents} onChange={e => set('maxStudents', parseInt(e.target.value) || 15)} style={inp} />
            </div>
          </div>

          {/* Teachers — only admins & test_prep teachers shown */}
          {cfg.dualTeacher ? (
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:6 }}>
                Teachers <span style={{ fontWeight:400, color:'#94a3b8' }}>— same person is allowed for both subjects</span>
              </div>
              <div style={{ fontSize:11, color:'#64748b', marginBottom:10, padding:'6px 10px', background:'#f8fafc', borderRadius:7, border:'1px solid rgba(0,0,0,0.07)' }}>
                👤 Only <strong>Admin</strong> and <strong>Test Prep Teachers</strong> appear below
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ ...lbl, color:'#2563eb' }}>📐 {cfg.t1Label} *</label>
                  <select value={form.teacher1Id} onChange={handleT1} style={{ ...inp, height:38, padding:'0 12px', borderColor:'#bfdbfe' }}>
                    <option value="">Select…</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}{t.role === 'admin' ? ' (Admin)' : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ ...lbl, color:'#7c3aed' }}>📖 {cfg.t2Label} *</label>
                  <select value={form.teacher2Id} onChange={handleT2} style={{ ...inp, height:38, padding:'0 12px', borderColor:'#ede9fe' }}>
                    <option value="">Select…</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}{t.role === 'admin' ? ' (Admin)' : ''}</option>)}
                  </select>
                  {form.teacher2Id && form.teacher2Id === form.teacher1Id && (
                    <div style={{ fontSize:11, color:'#d97706', marginTop:4 }}>⚠ Same teacher for both subjects</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label style={lbl}>Teacher / Instructor</label>
              <div style={{ fontSize:11, color:'#64748b', marginBottom:8, padding:'5px 9px', background:'#f8fafc', borderRadius:6, border:'1px solid rgba(0,0,0,0.07)' }}>
                👤 Only <strong>Admin</strong> and <strong>Test Prep Teachers</strong> appear below
              </div>
              <select value={form.teacher1Id} onChange={handleT1} style={{ ...inp, height:38, padding:'0 12px' }}>
                <option value="">Select teacher…</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}{t.role === 'admin' ? ' (Admin)' : ''}</option>)}
              </select>
            </div>
          )}

          {/* Schedule — dual (Math + Verbal) for SAT/ACT/GRE/GMAT, single for others */}
          {cfg.dualTeacher ? (
            <div style={{ background:'#f8fafc', borderRadius:12, padding:16, border:'1px solid rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:12 }}>📅 Class Schedule <span style={{ fontWeight:400, color:'#94a3b8' }}>— auto-shows on calendar weekly</span></div>

              {/* Math schedule */}
              <div style={{ marginBottom:14, background:'#eff6ff', borderRadius:10, padding:14, border:'1px solid #bfdbfe' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#2563eb', marginBottom:10 }}>📐 {cfg.t1Label} Class Days</div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
                  {DAY_LABELS.map((day, i) => {
                    const active = form.mathDays.includes(i);
                    return (
                      <button key={i} type="button" onClick={() => toggleMathDay(i)}
                        style={{ padding:'5px 11px', borderRadius:7, border:`1.5px solid ${active ? '#2563eb' : '#bfdbfe'}`, background:active ? '#2563eb' : '#fff', color:active ? '#fff' : '#374151', fontWeight:600, fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div><label style={{ ...lbl, fontSize:11, color:'#1d4ed8' }}>Start Time</label><input type="time" value={form.mathStartTime} onChange={e => set('mathStartTime', e.target.value)} style={{ ...inp, padding:'7px 10px', borderColor:'#bfdbfe' }} /></div>
                  <div><label style={{ ...lbl, fontSize:11, color:'#1d4ed8' }}>End Time</label><input type="time" value={form.mathEndTime} onChange={e => set('mathEndTime', e.target.value)} style={{ ...inp, padding:'7px 10px', borderColor:'#bfdbfe' }} /></div>
                </div>
                {form.mathDays.length > 0 && (
                  <div style={{ marginTop:8, fontSize:11, color:'#1e40af', background:'#dbeafe', borderRadius:6, padding:'5px 10px' }}>
                    ✓ {fmtDays(form.mathDays)}{form.mathStartTime && ` · ${fmtTime12(form.mathStartTime)}${form.mathEndTime ? `–${fmtTime12(form.mathEndTime)}` : ''}`}
                  </div>
                )}
              </div>

              {/* Verbal/English schedule */}
              <div style={{ background:'#f5f3ff', borderRadius:10, padding:14, border:'1px solid #ede9fe', marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#7c3aed', marginBottom:10 }}>📖 {cfg.t2Label} Class Days</div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 }}>
                  {DAY_LABELS.map((day, i) => {
                    const active = form.verbalDays.includes(i);
                    return (
                      <button key={i} type="button" onClick={() => toggleVerbalDay(i)}
                        style={{ padding:'5px 11px', borderRadius:7, border:`1.5px solid ${active ? '#7c3aed' : '#ede9fe'}`, background:active ? '#7c3aed' : '#fff', color:active ? '#fff' : '#374151', fontWeight:600, fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div><label style={{ ...lbl, fontSize:11, color:'#6d28d9' }}>Start Time</label><input type="time" value={form.verbalStartTime} onChange={e => set('verbalStartTime', e.target.value)} style={{ ...inp, padding:'7px 10px', borderColor:'#ede9fe' }} /></div>
                  <div><label style={{ ...lbl, fontSize:11, color:'#6d28d9' }}>End Time</label><input type="time" value={form.verbalEndTime} onChange={e => set('verbalEndTime', e.target.value)} style={{ ...inp, padding:'7px 10px', borderColor:'#ede9fe' }} /></div>
                </div>
                {form.verbalDays.length > 0 && (
                  <div style={{ marginTop:8, fontSize:11, color:'#6d28d9', background:'#ede9fe', borderRadius:6, padding:'5px 10px' }}>
                    ✓ {fmtDays(form.verbalDays)}{form.verbalStartTime && ` · ${fmtTime12(form.verbalStartTime)}${form.verbalEndTime ? `–${fmtTime12(form.verbalEndTime)}` : ''}`}
                  </div>
                )}
              </div>

              <div>
                <label style={lbl}>Batch Start Date</label>
                <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} style={inp} />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label style={lbl}>Class Days <span style={{ fontWeight:400, color:'#94a3b8' }}>— repeats automatically every week</span></label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {DAY_LABELS.map((day, i) => {
                    const active = form.scheduleDays.includes(i);
                    return (
                      <button key={i} type="button" onClick={() => toggleDay(i)}
                        style={{ padding:'6px 12px', borderRadius:8, border:`1.5px solid ${active ? '#7c3aed' : 'rgba(0,0,0,0.12)'}`, background:active ? '#7c3aed' : '#fff', color:active ? '#fff' : '#374151', fontWeight:600, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <div><label style={lbl}>Start Time</label><input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} style={inp} /></div>
                <div><label style={lbl}>End Time</label><input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Start Date</label><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} style={inp} /></div>
              </div>
              {form.scheduleDays.length > 0 && (
                <div style={{ padding:'10px 14px', background:'#f0fdf4', borderRadius:8, border:'1px solid #bbf7d0', fontSize:12, color:'#166534' }}>
                  📅 <strong>{fmtDays(form.scheduleDays)}</strong>
                  {form.startTime && <> · {fmtTime12(form.startTime)}{form.endTime && `–${fmtTime12(form.endTime)}`}</>}
                  {form.startDate && <> · Starting {form.startDate}</>}
                  <span style={{ color:'#94a3b8', marginLeft:6 }}>— Auto-shows on calendar every week until closed</span>
                </div>
              )}
            </>
          )}

          <div>
            <label style={lbl}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} placeholder="Optional notes…" style={{ ...inp, resize:'vertical' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => { if (!form.name.trim()) return; onSave(form); }} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            {editData ? 'Save Changes' : 'Create Class'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddSessionModal({ open, onClose, onSave, classes }) {
  const blank = { classId:'', date:today(), startTime:'', endTime:'', topic:'', notes:'' };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line
  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));
  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:20, color:'#0f172a' }}>Schedule Session</div>
        <div style={{ display:'grid', gap:14 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Class *</label>
            <select value={form.classId} onChange={e => set('classId', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
              <option value="">Select class…</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} ({c.testType})</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Date *</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inp} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Start</label>
              <input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} style={inp} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>End</label>
              <input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} style={inp} />
            </div>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Topic</label>
            <input value={form.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. Reading Comprehension Strategies" style={inp} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize:'vertical' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => { if (!form.classId || !form.date) return; onSave(form); }} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

function AddScoreModal({ open, onClose, onSave, students, classes }) {
  const blank = { studentId:'', classId:'', testType:'SAT', date:today(), sections:{}, notes:'' };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line
  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));
  const setSection = (s, v) => setForm(p => ({ ...p, sections:{ ...p.sections, [s]:v } }));
  const cfg = TEST_CFG[form.testType];
  const total = calcTotal(form.sections, form.testType);
  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16, overflowY:'auto' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', margin:'auto' }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:20, color:'#0f172a' }}>Record Score</div>
        <div style={{ display:'grid', gap:14 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Student *</label>
            <select value={form.studentId} onChange={e => set('studentId', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
              <option value="">Select student…</option>
              {students.map(s => <option key={s.id} value={s.id}>{getDisplayName(s)}</option>)}
            </select>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Test Type *</label>
              <select value={form.testType} onChange={e => { set('testType', e.target.value); set('sections', {}); }} style={{ ...inp, height:38, padding:'0 12px' }}>
                {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inp} />
            </div>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Class (optional)</label>
            <select value={form.classId} onChange={e => set('classId', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
              <option value="">None / Other</option>
              {classes.filter(c => c.testType === form.testType).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Section Scores <span style={{ fontWeight:400, color:'#94a3b8' }}>— {cfg?.note}</span></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {(cfg?.sections || []).map((s, i) => (
                <div key={s}>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>{s} (max {cfg.maxSec[i]})</label>
                  <input type="number" min="0" max={cfg.maxSec[i]} step={cfg.isBand ? '0.5' : '1'}
                    value={form.sections[s] ?? ''}
                    onChange={e => setSection(s, e.target.value)}
                    style={{ ...inp, padding:'8px 12px' }} />
                </div>
              ))}
            </div>
            {total !== null && (
              <div style={{ marginTop:10, padding:'10px 14px', background:cfg.bg, borderRadius:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:13, color:'#475569' }}>Calculated {cfg.isBand || cfg.composite ? 'Average' : 'Total'}</span>
                <span style={{ fontSize:18, fontWeight:800, color:cfg.color }}>{total} <span style={{ fontSize:12, fontWeight:400 }}>/ {cfg.maxTotal}</span></span>
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Performance notes, areas to improve…" style={{ ...inp, resize:'vertical' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => { if (!form.studentId) return; onSave(form); }} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            Save Score
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationModal({ open, onClose, onSend, students }) {
  const blank = { recipients:'all', studentId:'', channel:'whatsapp', subject:'', message:'' };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(blank); }, [open]); // eslint-disable-line
  const recipientList = useMemo(() => {
    if (form.recipients === 'one') return students.filter(s => s.id === form.studentId);
    return students;
  }, [form.recipients, form.studentId, students]);
  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));
  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  const channelEmoji = form.channel === 'whatsapp' ? '💬' : form.channel === 'email' ? '📧' : '📱';
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:500, boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:20, color:'#0f172a' }}>Send Parent Notification</div>
        <div style={{ display:'grid', gap:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Send To</label>
              <select value={form.recipients} onChange={e => set('recipients', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
                <option value="all">All Students ({students.length})</option>
                <option value="one">Specific Student</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Channel {channelEmoji}</label>
              <select value={form.channel} onChange={e => set('channel', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
          </div>
          {form.recipients === 'one' && (
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Select Student</label>
              <select value={form.studentId} onChange={e => set('studentId', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
                <option value="">Choose…</option>
                {students.map(s => <option key={s.id} value={s.id}>{getDisplayName(s)}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Subject</label>
            <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. SAT Score Update – Batch A" style={inp} />
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 }}>Message *</label>
            <textarea value={form.message} onChange={e => set('message', e.target.value)} rows={4} placeholder="Write your message to parents…" style={{ ...inp, resize:'vertical' }} />
          </div>
          <div style={{ fontSize:12, color:'#64748b', padding:'8px 12px', background:'#f8fafc', borderRadius:8 }}>
            Will be logged for <strong>{recipientList.length}</strong> student record{recipientList.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => { if (!form.message.trim()) return; onSend(form, recipientList); }} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 }}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Test Series Modal ────────────────────────────────────────────────────
function AddTestSeriesModal({ open, onClose, onSave, classId, classes }) {
  const blank = { classId: classId || '', type:'mock', name:'', date:today(), startTime:'09:00', endTime:'', notes:'' };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm({ ...blank, classId: classId || '' }); }, [open, classId]); // eslint-disable-line
  if (!open) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  const lbl = { fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 };
  const selType = SERIES_TYPES[form.type] || SERIES_TYPES.mock;
  const nameMap = { diagnostic:'Diagnostic Test', practice:'Practice Test', mock:'Mock Test', full:'Full Length Test' };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:520, boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:22 }}>
          <div style={{ width:48, height:48, borderRadius:14, background:selType.bg, border:`2px solid ${selType.badge}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>{selType.icon}</div>
          <div>
            <div style={{ fontWeight:800, fontSize:16, color:'#0f172a' }}>Add Test to Schedule</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:1 }}>Schedule a practice or mock test event</div>
          </div>
        </div>
        <div style={{ display:'grid', gap:14 }}>
          {/* Type grid */}
          <div>
            <label style={lbl}>Test Category</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              {Object.entries(SERIES_TYPES).map(([key, val]) => {
                const active = form.type === key;
                return (
                  <div key={key} onClick={() => { set('type', key); set('name', nameMap[key] || ''); }}
                    style={{ padding:'10px 12px', borderRadius:10, border:`1.5px solid ${active ? val.color : 'rgba(0,0,0,0.08)'}`, background:active ? val.bg : '#fafafa', cursor:'pointer', transition:'all 0.12s', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:18 }}>{val.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:active ? val.color : '#374151' }}>{val.label}</div>
                      <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>{val.desc}</div>
                    </div>
                    {active && <div style={{ width:16, height:16, borderRadius:'50%', background:val.color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><svg width="9" height="9" viewBox="0 0 9 9" fill="none"><polyline points="1.5,4.5 3.5,6.5 7.5,2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div>}
                  </div>
                );
              })}
            </div>
          </div>
          {!classId && (
            <div>
              <label style={lbl}>Class *</label>
              <select value={form.classId} onChange={e => set('classId', e.target.value)} style={{ ...inp, height:38, padding:'0 12px' }}>
                <option value="">Select class…</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name} ({c.testType})</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={lbl}>Test Name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder={nameMap[form.type] || 'Test name'} style={inp} />
          </div>
          <div>
            <label style={lbl}>Date *</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inp} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div><label style={lbl}>Start Time</label><input type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} style={inp} /></div>
            <div><label style={lbl}>End Time</label><input type="time" value={form.endTime} onChange={e => set('endTime', e.target.value)} style={inp} /></div>
          </div>
          <div>
            <label style={lbl}>Notes / Syllabus Coverage</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="e.g. Full Reading + Math sections, focus on time management…" style={{ ...inp, resize:'vertical' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => { const cid = form.classId || classId; if (!cid || !form.date) return; onSave({ ...form, classId:cid, name:form.name || nameMap[form.type] }); }}
            style={{ padding:'9px 20px', borderRadius:8, border:'none', background:selType.color, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:`0 3px 10px ${selType.color}40` }}>
            Add to Schedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Enrollment Modal ────────────────────────────────────────────────────
function EditEnrollmentModal({ open, onClose, onSave, enrollment, student, testType }) {
  const cfg = TEST_CFG[testType] || {};
  const [form, setForm] = useState({ actualTestDate:'', targetScore:'', notes:'' });
  useEffect(() => {
    if (open && enrollment) setForm({
      actualTestDate: enrollment.actualTestDate || '',
      targetScore: enrollment.targetScore || '',
      notes: enrollment.notes || '',
    });
  }, [open, enrollment]);
  if (!open || !enrollment) return null;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = { width:'100%', boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.12)', borderRadius:8, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', background:'#fafafa' };
  const lbl = { fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:5 };
  const d = daysUntil(form.actualTestDate);
  const urgColor = d === null ? '#64748b' : d <= 0 ? '#059669' : d <= 14 ? '#dc2626' : d <= 30 ? '#d97706' : '#059669';
  const pct = form.targetScore && cfg.maxTotal ? Math.min(100, Math.round((parseFloat(form.targetScore) / cfg.maxTotal) * 100)) : 0;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:28, width:'100%', maxWidth:460, boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontWeight:800, fontSize:16, color:'#0f172a' }}>Student Test Info</div>
          <div style={{ fontSize:13, color:'#64748b', marginTop:3 }}>{student ? getDisplayName(student) : ''} · {testType}</div>
        </div>
        <div style={{ display:'grid', gap:16 }}>
          {/* Actual test date */}
          <div>
            <label style={{ ...lbl, color:'#0f172a' }}>🎯 Actual Test Date</label>
            <input type="date" value={form.actualTestDate} onChange={e => set('actualTestDate', e.target.value)} style={inp} />
            {form.actualTestDate && (
              <div style={{ marginTop:8, padding:'10px 14px', borderRadius:10, background: d !== null && d <= 14 ? 'rgba(239,68,68,0.07)' : d !== null && d <= 30 ? 'rgba(245,158,11,0.07)' : 'rgba(5,150,105,0.07)', border:`1px solid ${urgColor}30`, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:22 }}>{d !== null && d <= 0 ? '✅' : d !== null && d <= 14 ? '🔴' : d !== null && d <= 30 ? '🟡' : '🟢'}</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:urgColor }}>
                    {d === null ? '' : d <= 0 ? 'Test date has passed' : `${d} days to test day`}
                  </div>
                  <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>{fmtShortDate(form.actualTestDate)}</div>
                </div>
              </div>
            )}
          </div>
          {/* Target score */}
          <div>
            <label style={lbl}>🏆 Target Score <span style={{ fontWeight:400, color:'#94a3b8' }}>({cfg.note})</span></label>
            <input type="number" min="0" max={cfg.maxTotal || 9999} step={cfg.isBand ? '0.5' : '1'}
              value={form.targetScore} onChange={e => set('targetScore', e.target.value)}
              placeholder={cfg.maxTotal ? `e.g. ${Math.round(cfg.maxTotal * 0.8)}` : 'Score'} style={inp} />
            {pct > 0 && (
              <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                <div style={{ flex:1, height:8, background:'rgba(0,0,0,0.07)', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:`linear-gradient(90deg,${cfg.color || '#7c3aed'}80,${cfg.color || '#7c3aed'})`, borderRadius:4 }} />
                </div>
                <span style={{ fontSize:12, color:cfg.color || '#7c3aed', fontWeight:700, minWidth:42 }}>{pct}%</span>
              </div>
            )}
          </div>
          {/* Notes */}
          <div>
            <label style={lbl}>📋 Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="e.g. Targeting top-50 university, needs to strengthen Math…" style={{ ...inp, resize:'vertical' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:22, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.12)', background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={() => onSave(enrollment.id, form)}
            style={{ padding:'9px 20px', borderRadius:8, border:'none', background:'linear-gradient(135deg,#7c3aed,#6d28d9)', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, boxShadow:'0 3px 10px rgba(124,58,237,0.4)' }}>
            Save Info
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TestPrep() {
  const { leads, currentUser, companyId, users } = useCRM();

  // UI state
  const [tab, setTab] = useState('dashboard');
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showAddClass, setShowAddClass] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddScore, setShowAddScore] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [weekOffset, setWeekOffset] = useState(0);
  const [enrollMode, setEnrollMode] = useState(false);
  const [showAddTestSeries, setShowAddTestSeries] = useState(false);
  const [showEditEnrollment, setShowEditEnrollment] = useState(false);
  const [editingEnrollmentData, setEditingEnrollmentData] = useState(null); // { enrollment, student }

  // Firestore collections
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [scores, setScores] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [testSeries, setTestSeries] = useState([]);

  const role = currentUser?.role;
  const isAdmin = role === 'admin' || role === 'superadmin';
  const canEdit = isAdmin || role === 'test_prep';

  // ─── Subscriptions ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, 'companies', companyId, 'testPrepClasses'), orderBy('createdAt', 'desc')), snap => setClasses(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, 'companies', companyId, 'testPrepSessions'), orderBy('date', 'asc')), snap => setSessions(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, 'companies', companyId, 'testPrepScores'), orderBy('date', 'desc')), snap => setScores(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(collection(db, 'companies', companyId, 'testPrepEnrollments'), snap => setEnrollments(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, 'companies', companyId, 'testPrepNotifications'), orderBy('sentAt', 'desc')), snap => setNotifications(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    return onSnapshot(query(collection(db, 'companies', companyId, 'testPrepTestSeries'), orderBy('date', 'asc')), snap => setTestSeries(snap.docs.map(d => ({ id:d.id, ...d.data() }))));
  }, [companyId]);

  // ─── Computed ──────────────────────────────────────────────────────────────
  const teachers = useMemo(() => (users || []).filter(u => u.role === 'test_prep' || u.role === 'admin'), [users]);

  const enrolledLeadIds = useMemo(() => new Set(enrollments.map(e => e.leadId)), [enrollments]);

  const allStudents = useMemo(() => (leads || []).filter(l => enrolledLeadIds.has(l.id) && getDisplayName(l)).sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))), [leads, enrolledLeadIds]);

  const allLeadsWithName = useMemo(() => (leads || []).filter(l => getDisplayName(l)).sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))), [leads]);

  const visibleStudents = useMemo(() => {
    if (!studentSearch) return allStudents;
    const q = studentSearch.toLowerCase();
    return allStudents.filter(s => getDisplayName(s).toLowerCase().includes(q));
  }, [allStudents, studentSearch]);

  const classColorMap = useMemo(() => {
    const map = {};
    classes.forEach((c, i) => { map[c.id] = CLASS_COLORS[i % CLASS_COLORS.length]; });
    return map;
  }, [classes]);

  const selectedClass = useMemo(() => classes.find(c => c.id === selectedClassId) || null, [classes, selectedClassId]);

  const classEnrollmentIds = useMemo(() => new Set(enrollments.filter(e => e.classId === selectedClassId).map(e => e.leadId)), [enrollments, selectedClassId]);

  const classStudents = useMemo(() => (leads || []).filter(l => classEnrollmentIds.has(l.id)).sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))), [leads, classEnrollmentIds]);

  const weekStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + weekOffset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [weekOffset]);

  const weekDays = useMemo(() => Array.from({ length:7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; }), [weekStart]);

  // Auto-derive recurring sessions from class schedules for the current week view
  // For dual-teacher types (SAT/ACT/GRE/GMAT): generates separate Math + Verbal events
  const derivedCalendarSessions = useMemo(() => {
    const result = [];
    weekDays.forEach(day => {
      const dayIndex = day.getDay();
      const dateStr = day.toISOString().split('T')[0];
      classes.forEach(cls => {
        if (cls.status === 'closed') return;
        if (cls.startDate && dateStr < cls.startDate) return;
        const cfg = TEST_CFG[cls.testType] || {};
        if (cfg.dualTeacher) {
          // Math/Quant session
          if ((cls.mathDays || []).includes(dayIndex)) {
            result.push({
              id: `recurring-math-${cls.id}-${dateStr}`,
              classId: cls.id, className: cls.name, testType: cls.testType,
              date: dateStr, startTime: cls.mathStartTime || '', endTime: cls.mathEndTime || '',
              topic: cfg.t1Label || 'Math', sessionSubject: 'math',
              teacherName: cls.teacher1Name || '', isRecurring: true,
            });
          }
          // Verbal/English session
          if ((cls.verbalDays || []).includes(dayIndex)) {
            result.push({
              id: `recurring-verbal-${cls.id}-${dateStr}`,
              classId: cls.id, className: cls.name, testType: cls.testType,
              date: dateStr, startTime: cls.verbalStartTime || '', endTime: cls.verbalEndTime || '',
              topic: cfg.t2Label || 'Verbal', sessionSubject: 'verbal',
              teacherName: cls.teacher2Name || '', isRecurring: true,
            });
          }
        } else {
          // Single schedule for TOEFL/IELTS/PTE/DET
          if (!(cls.scheduleDays || []).includes(dayIndex)) return;
          result.push({
            id: `recurring-${cls.id}-${dateStr}`,
            classId: cls.id, className: cls.name, testType: cls.testType,
            date: dateStr, startTime: cls.startTime || '', endTime: cls.endTime || '',
            topic: '', teacherName: cls.teacher1Name || '', isRecurring: true,
          });
        }
      });
    });
    return result;
  }, [classes, weekDays]);

  const weekSessions = useMemo(() => {
    const start = weekStart.toISOString().split('T')[0];
    const end = weekDays[6].toISOString().split('T')[0];
    const manual = sessions.filter(s => s.date >= start && s.date <= end);
    // Skip derived session if a manual one already exists for same class + date
    const derived = derivedCalendarSessions.filter(d => !manual.some(m => m.classId === d.classId && m.date === d.date));
    return [...derived, ...manual].sort((a, b) => (a.date).localeCompare(b.date) || (a.startTime || '').localeCompare(b.startTime || ''));
  }, [sessions, derivedCalendarSessions, weekStart, weekDays]);

  const classTestSeries = useMemo(() => testSeries.filter(ts => ts.classId === selectedClassId), [testSeries, selectedClassId]);

  const selectedStudent = useMemo(() => (leads || []).find(l => l.id === selectedStudentId) || null, [leads, selectedStudentId]);

  const studentScores = useMemo(() => scores.filter(s => s.studentId === selectedStudentId), [scores, selectedStudentId]);

  const filteredScores = useMemo(() => scoreFilter === 'all' ? scores : scores.filter(s => s.testType === scoreFilter), [scores, scoreFilter]);

  // ─── CRUD ──────────────────────────────────────────────────────────────────
  const saveClass = useCallback(async (form) => {
    if (!companyId) return;
    if (editingClass) {
      await updateDoc(doc(db, 'companies', companyId, 'testPrepClasses', editingClass.id), { ...form, updatedAt:serverTimestamp() });
    } else {
      await addDoc(collection(db, 'companies', companyId, 'testPrepClasses'), { ...form, companyId, createdAt:serverTimestamp(), updatedAt:serverTimestamp() });
    }
    setShowAddClass(false);
    setEditingClass(null);
  }, [companyId, editingClass]);

  const deleteClass = useCallback(async (classId) => {
    if (!companyId || !window.confirm('Delete this class? Students and scores will be unaffected.')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'testPrepClasses', classId));
    if (selectedClassId === classId) setSelectedClassId(null);
  }, [companyId, selectedClassId]);

  const toggleClassStatus = useCallback(async (cls) => {
    if (!companyId) return;
    const newStatus = cls.status === 'closed' ? 'active' : 'closed';
    await updateDoc(doc(db, 'companies', companyId, 'testPrepClasses', cls.id), {
      status: newStatus, updatedAt: serverTimestamp(),
    });
  }, [companyId]);

  const saveSession = useCallback(async (form) => {
    if (!companyId) return;
    const cls = classes.find(c => c.id === form.classId);
    await addDoc(collection(db, 'companies', companyId, 'testPrepSessions'), {
      ...form, className:cls?.name || '', testType:cls?.testType || '',
      teacher1Id:cls?.teacher1Id || '', teacher1Name:cls?.teacher1Name || '',
      teacher2Id:cls?.teacher2Id || '', teacher2Name:cls?.teacher2Name || '',
      companyId, createdAt:serverTimestamp(),
    });
    setShowAddSession(false);
  }, [companyId, classes]);

  const saveScore = useCallback(async (form) => {
    if (!companyId) return;
    const student = (leads || []).find(l => l.id === form.studentId);
    const cls = classes.find(c => c.id === form.classId);
    const total = calcTotal(form.sections, form.testType);
    await addDoc(collection(db, 'companies', companyId, 'testPrepScores'), {
      ...form, studentName:getDisplayName(student) || '',
      className:cls?.name || '', total, companyId,
      createdBy:currentUser?.id || '', createdAt:serverTimestamp(),
    });
    setShowAddScore(false);
  }, [companyId, leads, classes, currentUser]);

  const toggleEnroll = useCallback(async (leadId) => {
    if (!companyId || !selectedClassId) return;
    const existing = enrollments.find(e => e.classId === selectedClassId && e.leadId === leadId);
    if (existing) {
      await deleteDoc(doc(db, 'companies', companyId, 'testPrepEnrollments', existing.id));
    } else {
      const student = (leads || []).find(l => l.id === leadId);
      const cls = classes.find(c => c.id === selectedClassId);
      await addDoc(collection(db, 'companies', companyId, 'testPrepEnrollments'), {
        classId:selectedClassId, leadId, studentName:getDisplayName(student) || '',
        companyId, enrolledAt:serverTimestamp(),
        // Attach teacher info from the class so student profile can show their teachers
        testType:cls?.testType || '',
        teacher1Id:cls?.teacher1Id || '', teacher1Name:cls?.teacher1Name || '',
        teacher2Id:cls?.teacher2Id || '', teacher2Name:cls?.teacher2Name || '',
        teacher1Label:TEST_CFG[cls?.testType]?.t1Label || '',
        teacher2Label:TEST_CFG[cls?.testType]?.t2Label || '',
      });
    }
  }, [companyId, selectedClassId, enrollments, leads, classes]);

  const sendNotification = useCallback(async (form, recipientStudents) => {
    if (!companyId) return;
    await addDoc(collection(db, 'companies', companyId, 'testPrepNotifications'), {
      ...form, sentBy:currentUser?.name || '', sentById:currentUser?.id || '',
      recipientCount:recipientStudents.length,
      recipientIds:recipientStudents.map(s => s.id),
      companyId, sentAt:serverTimestamp(),
    });
    setShowNotification(false);
  }, [companyId, currentUser]);

  const saveTestSeriesItem = useCallback(async (form) => {
    if (!companyId) return;
    await addDoc(collection(db, 'companies', companyId, 'testPrepTestSeries'), { ...form, companyId, createdAt:serverTimestamp() });
    setShowAddTestSeries(false);
  }, [companyId]);

  const deleteTestSeriesItem = useCallback(async (id) => {
    if (!companyId || !window.confirm('Remove this test from the schedule?')) return;
    await deleteDoc(doc(db, 'companies', companyId, 'testPrepTestSeries', id));
  }, [companyId]);

  const updateEnrollment = useCallback(async (enrollmentId, data) => {
    if (!companyId) return;
    await updateDoc(doc(db, 'companies', companyId, 'testPrepEnrollments', enrollmentId), data);
    setShowEditEnrollment(false);
    setEditingEnrollmentData(null);
  }, [companyId]);

  // ─── Render: Dashboard ─────────────────────────────────────────────────────
  const renderDashboard = () => {
    const scoresByType = {};
    TEST_TYPES.forEach(t => { scoresByType[t] = scores.filter(s => s.testType === t).length; });
    return (
      <div style={{ padding:24 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:16, marginBottom:28 }}>
          <StatCard label="Total Students" value={allStudents.length} icon="🎓" color="#7c3aed" />
          <StatCard label="Active Classes" value={classes.length} icon="📚" color="#2563eb" />
          <StatCard label="Sessions This Week" value={weekSessions.length} icon="📅" color="#059669" />
          <StatCard label="Scores Recorded" value={scores.length} icon="📊" color="#d97706" />
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          {/* Test type breakdown */}
          <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:16 }}>Scores by Test</div>
            {TEST_TYPES.map(t => {
              const count = scoresByType[t] || 0;
              const cfg = TEST_CFG[t];
              const pct = scores.length ? (count / scores.length) * 100 : 0;
              return (
                <div key={t} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <span style={{ width:36, textAlign:'center', padding:'2px 6px', borderRadius:5, background:cfg.badge, fontSize:10, fontWeight:700, color:cfg.color, flexShrink:0 }}>{t}</span>
                  <div style={{ flex:1, height:5, background:'rgba(0,0,0,0.07)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:cfg.color, borderRadius:3 }} />
                  </div>
                  <span style={{ fontSize:12, color:'#64748b', minWidth:16, textAlign:'right' }}>{count}</span>
                </div>
              );
            })}
          </div>

          {/* This week's sessions */}
          <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:16 }}>This Week's Sessions</div>
            {weekSessions.length === 0 ? (
              <div style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'24px 0' }}>No sessions this week</div>
            ) : weekSessions.slice(0, 6).map(s => {
              const color = classColorMap[s.classId] || '#7c3aed';
              return (
                <div key={s.id} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10, paddingBottom:10, borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ width:4, borderRadius:2, background:color, alignSelf:'stretch', minHeight:28, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{s.className}</div>
                    <div style={{ fontSize:11, color:'#64748b' }}>{s.date}{s.startTime ? ` · ${s.startTime}` : ''}{s.endTime ? `–${s.endTime}` : ''}</div>
                    {s.topic && <div style={{ fontSize:11, color:'#94a3b8' }}>{s.topic}</div>}
                  </div>
                  {s.testType && <span style={{ padding:'2px 6px', borderRadius:5, background:TEST_CFG[s.testType]?.badge, fontSize:10, fontWeight:700, color:TEST_CFG[s.testType]?.color, flexShrink:0 }}>{s.testType}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent scores */}
        <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Recent Scores</div>
            {canEdit && <button onClick={() => setShowAddScore(true)} style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:600, fontSize:12, cursor:'pointer' }}>+ Add Score</button>}
          </div>
          {scores.length === 0 ? (
            <div style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'28px 0' }}>No scores recorded yet</div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>{['Student','Test','Score','Date','Notes'].map(h => <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'#64748b', fontWeight:600, borderBottom:'1px solid rgba(0,0,0,0.07)' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {scores.slice(0, 10).map(sc => {
                    const cfg = TEST_CFG[sc.testType] || {};
                    const pct = getScorePercent(sc.total, sc.testType);
                    return (
                      <tr key={sc.id} style={{ borderBottom:'1px solid rgba(0,0,0,0.04)' }}>
                        <td style={{ padding:'10px 12px', fontWeight:600, color:'#0f172a', cursor:'pointer' }} onClick={() => { setSelectedStudentId(sc.studentId); setTab('students'); }}>{sc.studentName || '—'}</td>
                        <td style={{ padding:'10px 12px' }}><span style={{ padding:'2px 8px', borderRadius:6, background:cfg.badge, fontSize:11, fontWeight:700, color:cfg.color }}>{sc.testType}</span></td>
                        <td style={{ padding:'10px 12px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontWeight:700, color:cfg.color }}>{sc.total ?? '–'}</span>
                            <span style={{ fontSize:11, color:'#94a3b8' }}>/{cfg.maxTotal}</span>
                            <div style={{ width:36, height:4, background:'rgba(0,0,0,0.07)', borderRadius:2 }}><div style={{ width:`${pct}%`, height:'100%', background:cfg.color, borderRadius:2 }} /></div>
                          </div>
                        </td>
                        <td style={{ padding:'10px 12px', color:'#64748b' }}>{typeof sc.date === 'string' ? sc.date : fmtDate(sc.date)}</td>
                        <td style={{ padding:'10px 12px', color:'#94a3b8', maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{sc.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: Students ──────────────────────────────────────────────────────
  const renderStudents = () => {
    if (selectedStudentId && selectedStudent) {
      const studentEnrolledClasses = enrollments.filter(e => e.leadId === selectedStudentId).map(e => classes.find(c => c.id === e.classId)).filter(Boolean);
      return (
        <div style={{ padding:24 }}>
          <button onClick={() => setSelectedStudentId(null)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:12, color:'#374151', marginBottom:20 }}>
            ← Back to Students
          </button>
          <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:20 }}>
            {/* Left: profile */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ width:52, height:52, borderRadius:14, background:'linear-gradient(135deg,#7c3aed,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:800, color:'#fff', marginBottom:12 }}>
                  {getDisplayName(selectedStudent).charAt(0) || '?'}
                </div>
                <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>{getDisplayName(selectedStudent)}</div>
                {selectedStudent.email && <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>{selectedStudent.email}</div>}
                {selectedStudent.phone && <div style={{ fontSize:12, color:'#64748b' }}>{selectedStudent.phone}</div>}
                <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid rgba(0,0,0,0.07)' }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.05em' }}>Enrolled Classes</div>
                  {studentEnrolledClasses.length === 0 ? <div style={{ fontSize:12, color:'#94a3b8' }}>Not enrolled in any class</div> : studentEnrolledClasses.map(c => {
                    const cfg = TEST_CFG[c.testType] || {};
                    const enr = enrollments.find(e => e.classId === c.id && e.leadId === selectedStudentId);
                    return (
                      <div key={c.id} style={{ marginBottom:10, paddingBottom:10, borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <div style={{ width:8, height:8, borderRadius:'50%', background:classColorMap[c.id] || '#7c3aed', flexShrink:0 }} />
                          <div style={{ fontSize:13, color:'#374151', flex:1, fontWeight:600 }}>{c.name}</div>
                          <span style={{ padding:'1px 6px', borderRadius:4, background:cfg.badge, fontSize:10, fontWeight:700, color:cfg.color }}>{c.testType}</span>
                        </div>
                        {/* Teacher chips */}
                        {(enr?.teacher1Name || c.teacher1Name) && (
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap', paddingLeft:16, marginTop:2 }}>
                            {(enr?.teacher1Name || c.teacher1Name) && (
                              <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'#eff6ff', color:'#1e40af', border:'1px solid #bfdbfe', fontWeight:600 }}>
                                📐 {enr?.teacher1Name || c.teacher1Name}
                                {(enr?.teacher1Label || cfg.t1Label) && <span style={{ fontWeight:400, color:'#64748b' }}> · {enr?.teacher1Label || cfg.t1Label}</span>}
                              </span>
                            )}
                            {(enr?.teacher2Name || c.teacher2Name) && (enr?.teacher2Name || c.teacher2Name) !== (enr?.teacher1Name || c.teacher1Name) && (
                              <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'#f5f3ff', color:'#6d28d9', border:'1px solid #ede9fe', fontWeight:600 }}>
                                📖 {enr?.teacher2Name || c.teacher2Name}
                                {(enr?.teacher2Label || cfg.t2Label) && <span style={{ fontWeight:400, color:'#64748b' }}> · {enr?.teacher2Label || cfg.t2Label}</span>}
                              </span>
                            )}
                            {(enr?.teacher2Name || c.teacher2Name) && (enr?.teacher2Name || c.teacher2Name) === (enr?.teacher1Name || c.teacher1Name) && (
                              <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, background:'#f0fdf4', color:'#166534', border:'1px solid #bbf7d0', fontWeight:600 }}>
                                👤 {enr?.teacher1Name || c.teacher1Name} (both subjects)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Countdown cards per class enrollment */}
              {studentEnrolledClasses.map(c => {
                const enr = enrollments.find(e => e.classId === c.id && e.leadId === selectedStudentId);
                if (!enr?.actualTestDate) return null;
                const d = daysUntil(enr.actualTestDate);
                const cfg = TEST_CFG[c.testType] || {};
                const sScores = scores.filter(s => s.studentId === selectedStudentId && s.testType === c.testType);
                const best = sScores.length ? sScores.reduce((a, b) => ((b.total || 0) > (a.total || 0) ? b : a), sScores[0]) : null;
                const target = enr.targetScore ? parseFloat(enr.targetScore) : null;
                const gap = target && best?.total != null ? target - parseFloat(best.total) : null;
                const urgColor = d === null ? cfg.color : d <= 0 ? '#059669' : d <= 14 ? '#dc2626' : d <= 30 ? '#d97706' : cfg.color;
                return (
                  <div key={c.id} style={{ background:`linear-gradient(135deg,${cfg.color}12,${cfg.color}04)`, borderRadius:14, padding:16, border:`1.5px solid ${cfg.color}25` }}>
                    <div style={{ fontSize:10, fontWeight:800, color:cfg.color, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
                      {c.testType} · {c.name}
                    </div>
                    {/* Countdown */}
                    <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:6 }}>
                      <div style={{ fontSize:40, fontWeight:900, color:urgColor, lineHeight:1 }}>
                        {d !== null && d <= 0 ? '✅' : d ?? '—'}
                      </div>
                      {d !== null && d > 0 && <div style={{ fontSize:13, fontWeight:600, color:'#64748b' }}>days to test</div>}
                      {d !== null && d <= 0 && <div style={{ fontSize:13, fontWeight:600, color:'#059669' }}>Test date reached!</div>}
                    </div>
                    <div style={{ fontSize:11, color:'#64748b', marginBottom:10 }}>{fmtShortDate(enr.actualTestDate)}</div>
                    {/* Progress bar */}
                    {target && (
                      <div>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#64748b', marginBottom:4 }}>
                          <span>Score Progress</span>
                          <span style={{ color: gap !== null && gap <= 0 ? '#059669' : '#d97706', fontWeight:700 }}>
                            {best?.total ?? 0} → {target} (target)
                          </span>
                        </div>
                        <div style={{ height:8, background:'rgba(0,0,0,0.07)', borderRadius:4, overflow:'hidden' }}>
                          <div style={{ width:`${best?.total && cfg.maxTotal ? Math.min(100, (parseFloat(best.total)/cfg.maxTotal)*100) : 0}%`, height:'100%', background:urgColor, borderRadius:4, transition:'width 0.5s' }} />
                        </div>
                        {gap !== null && (
                          <div style={{ marginTop:6, fontSize:11, fontWeight:700, color: gap <= 0 ? '#059669' : '#d97706' }}>
                            {gap <= 0 ? '✓ Target achieved!' : `${Math.abs(gap).toFixed(0)} more points needed`}
                          </div>
                        )}
                      </div>
                    )}
                    {canEdit && (
                      <button onClick={() => { setEditingEnrollmentData({ enrollment:enr, student:selectedStudent }); setShowEditEnrollment(true); }}
                        style={{ marginTop:10, padding:'5px 12px', borderRadius:7, border:`1px solid ${cfg.color}30`, background:'rgba(255,255,255,0.7)', color:cfg.color, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                        ✏️ Edit Test Info
                      </button>
                    )}
                  </div>
                );
              })}
              {/* Best scores summary */}
              <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#0f172a', marginBottom:14 }}>Score Summary</div>
                {TEST_TYPES.map(tt => {
                  const ttScores = studentScores.filter(s => s.testType === tt);
                  if (!ttScores.length) return null;
                  const best = ttScores.reduce((a, b) => ((b.total || 0) > (a.total || 0) ? b : a), ttScores[0]);
                  const cfg = TEST_CFG[tt] || {};
                  return (
                    <div key={tt} style={{ marginBottom:14, padding:12, borderRadius:10, background:cfg.bg, border:`1px solid ${cfg.badge}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:cfg.color }}>{tt}</span>
                        <span style={{ fontSize:11, color:'#64748b' }}>{ttScores.length} attempt{ttScores.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ fontSize:22, fontWeight:800, color:cfg.color }}>{best.total ?? '–'} <span style={{ fontSize:12, fontWeight:400, color:'#64748b' }}>/ {cfg.maxTotal}</span></div>
                      <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>Best · {cfg.note}</div>
                      <ScoreSparkline scores={ttScores} testType={tt} />
                    </div>
                  );
                }).filter(Boolean)}
                {studentScores.length === 0 && <div style={{ fontSize:12, color:'#94a3b8', textAlign:'center', padding:'16px 0' }}>No scores yet</div>}
              </div>
            </div>

            {/* Right: score history */}
            <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Score History</div>
                {canEdit && <button onClick={() => setShowAddScore(true)} style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:600, fontSize:12, cursor:'pointer' }}>+ Add Score</button>}
              </div>
              {studentScores.length === 0 ? (
                <div style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'40px 0' }}>No scores recorded for this student</div>
              ) : studentScores.map(sc => {
                const cfg = TEST_CFG[sc.testType] || {};
                const total = sc.total ?? calcTotal(sc.sections, sc.testType);
                const pct = getScorePercent(total, sc.testType);
                const perfColor = pct >= 80 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
                return (
                  <div key={sc.id} style={{ marginBottom:16, padding:16, borderRadius:12, border:`1.5px solid ${cfg.badge}`, background:cfg.bg }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <span style={{ padding:'3px 10px', borderRadius:6, background:cfg.color, fontSize:12, fontWeight:700, color:'#fff' }}>{sc.testType}</span>
                        {sc.className && <span style={{ fontSize:12, color:'#64748b' }}>{sc.className}</span>}
                      </div>
                      <div style={{ fontSize:12, color:'#64748b' }}>{typeof sc.date === 'string' ? sc.date : fmtDate(sc.date)}</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:10 }}>
                      <div style={{ fontSize:28, fontWeight:800, color:cfg.color }}>{total ?? '–'}</div>
                      <div>
                        <div style={{ fontSize:11, color:'#64748b' }}>/ {cfg.maxTotal}</div>
                        <div style={{ fontSize:11, fontWeight:600, color:perfColor }}>{pct}% · {pct >= 80 ? 'Excellent' : pct >= 60 ? 'Good' : pct >= 40 ? 'Average' : 'Needs Work'}</div>
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                      {(cfg.sections || []).map((s, i) => (
                        <div key={s}>
                          <div style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>{s}</div>
                          <ScoreBar value={parseFloat(sc.sections?.[s]) || 0} max={cfg.maxSec?.[i] || 100} color={cfg.color} />
                        </div>
                      ))}
                    </div>
                    {sc.notes && <div style={{ fontSize:12, color:'#475569', padding:'8px 10px', background:'rgba(255,255,255,0.7)', borderRadius:8 }}>{sc.notes}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>Students</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{allStudents.length} enrolled</div>
          </div>
        </div>
        <input value={studentSearch} onChange={e => setStudentSearch(e.target.value)} placeholder="Search students…"
          style={{ width:'100%', maxWidth:340, boxSizing:'border-box', border:'1.5px solid rgba(0,0,0,0.1)', borderRadius:10, fontSize:13, padding:'10px 14px', marginBottom:18, fontFamily:'inherit', outline:'none' }} />
        {visibleStudents.length === 0 ? (
          <div style={{ textAlign:'center', padding:'72px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🎓</div>
            <div style={{ fontSize:15, fontWeight:600 }}>No students yet</div>
            <div style={{ fontSize:13, marginTop:4 }}>Enroll leads into a class to see them here</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:14 }}>
            {visibleStudents.map(student => {
              const sScores = scores.filter(s => s.studentId === student.id);
              const classCount = enrollments.filter(e => e.leadId === student.id).length;
              const testTypes = [...new Set(sScores.map(s => s.testType))];
              return (
                <div key={student.id} onClick={() => setSelectedStudentId(student.id)}
                  style={{ background:'#fff', borderRadius:12, padding:16, cursor:'pointer', border:'1.5px solid rgba(0,0,0,0.07)', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', transition:'box-shadow 0.2s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = ''; }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                    <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#7c3aed,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:800, color:'#fff', flexShrink:0 }}>
                      {getDisplayName(student).charAt(0) || '?'}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, color:'#0f172a', fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{getDisplayName(student)}</div>
                      <div style={{ fontSize:11, color:'#64748b' }}>{classCount} class{classCount !== 1 ? 'es' : ''} · {sScores.length} score{sScores.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  {testTypes.length > 0 && (
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                      {testTypes.map(tt => {
                        const cfg = TEST_CFG[tt] || {};
                        const best = sScores.filter(s => s.testType === tt).reduce((a, b) => ((b.total || 0) > (a.total || 0) ? b : a), { total:0 });
                        return <span key={tt} style={{ padding:'2px 8px', borderRadius:6, background:cfg.badge, fontSize:11, fontWeight:700, color:cfg.color }}>{tt} {best.total || '–'}</span>;
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Classes ───────────────────────────────────────────────────────
  const renderClasses = () => {
    if (selectedClassId && selectedClass) {
      return (
        <div style={{ padding:24 }}>
          {/* Class detail header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <button onClick={() => { setSelectedClassId(null); setEnrollMode(false); }} style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:12 }}>← Back</button>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ fontSize:17, fontWeight:800, color:'#0f172a' }}>{selectedClass.name}</div>
                  {selectedClass.status === 'closed' && (
                    <span style={{ padding:'2px 8px', borderRadius:6, background:'rgba(239,68,68,0.1)', fontSize:11, fontWeight:700, color:'#dc2626' }}>CLOSED</span>
                  )}
                </div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
                  {selectedClass.testType}
                  {(() => {
                    const cfg = TEST_CFG[selectedClass.testType] || {};
                    const teachers = [selectedClass.teacher1Name, selectedClass.teacher2Name].filter(Boolean);
                    const uniqueTeachers = [...new Set(teachers)];
                    if (uniqueTeachers.length) return ` · ${uniqueTeachers.join(' & ')}`;
                    return '';
                  })()}
                  {(() => {
                    const cfg = TEST_CFG[selectedClass.testType] || {};
                    if (cfg.dualTeacher) {
                      const parts = [];
                      if (selectedClass.mathDays?.length) parts.push(`Math: ${fmtDays(selectedClass.mathDays)}`);
                      if (selectedClass.verbalDays?.length) parts.push(`Verbal: ${fmtDays(selectedClass.verbalDays)}`);
                      return parts.length ? ` · ${parts.join(', ')}` : '';
                    }
                    return selectedClass.scheduleDays?.length > 0 ? ` · ${fmtDays(selectedClass.scheduleDays)}` : '';
                  })()}
                </div>
              </div>
            </div>
            {canEdit && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
                <button onClick={() => setEnrollMode(!enrollMode)} style={{ padding:'8px 14px', borderRadius:8, border:`1.5px solid ${enrollMode ? '#7c3aed' : 'rgba(0,0,0,0.1)'}`, background:enrollMode ? '#7c3aed' : '#fff', color:enrollMode ? '#fff' : '#374151', fontWeight:600, fontSize:12, cursor:'pointer' }}>
                  {enrollMode ? '✓ Done' : '+ Enroll'}
                </button>
                <button onClick={() => { setEditingClass(selectedClass); setShowAddClass(true); }} style={{ padding:'8px 14px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:12 }}>Edit</button>
                <button onClick={() => toggleClassStatus(selectedClass)}
                  style={{ padding:'8px 14px', borderRadius:8, border:`1.5px solid ${selectedClass.status === 'closed' ? '#059669' : '#dc2626'}`, background:selectedClass.status === 'closed' ? 'rgba(5,150,105,0.08)' : 'rgba(239,68,68,0.08)', color:selectedClass.status === 'closed' ? '#059669' : '#dc2626', fontWeight:600, fontSize:12, cursor:'pointer' }}>
                  {selectedClass.status === 'closed' ? '▶ Reopen' : '■ Close Class'}
                </button>
              </div>
            )}
          </div>

          {/* Teacher badges for dual-teacher tests */}
          {(() => {
            const cfg = TEST_CFG[selectedClass.testType];
            if (!cfg?.dualTeacher) return null;
            return (
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                <div style={{ padding:'8px 14px', borderRadius:10, background:'#eff6ff', border:'1.5px solid #bfdbfe', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#2563eb', textTransform:'uppercase', letterSpacing:'0.04em' }}>{cfg.t1Label}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:'#1e40af' }}>{selectedClass.teacher1Name || 'Not assigned'}</span>
                </div>
                <div style={{ padding:'8px 14px', borderRadius:10, background:'#f5f3ff', border:'1.5px solid #ede9fe', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.04em' }}>{cfg.t2Label}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:'#6d28d9' }}>{selectedClass.teacher2Name || 'Not assigned'}</span>
                  {selectedClass.teacher2Id && selectedClass.teacher2Id === selectedClass.teacher1Id && <span style={{ fontSize:10, color:'#d97706' }}>(same)</span>}
                </div>
              </div>
            );
          })()}

          {/* Info tiles */}
          {(() => {
            const cfg = TEST_CFG[selectedClass.testType] || {};
            const tiles = cfg.dualTeacher ? [
              { label:'Test Type', value:selectedClass.testType, color:cfg.color || '#374151' },
              { label:`📐 ${cfg.t1Label || 'Math'} Days`, value:selectedClass.mathDays?.length ? fmtDays(selectedClass.mathDays) : 'Not set', sub: selectedClass.mathStartTime ? `${fmtTime12(selectedClass.mathStartTime)}${selectedClass.mathEndTime ? `–${fmtTime12(selectedClass.mathEndTime)}` : ''}` : '', color:'#2563eb' },
              { label:`📖 ${cfg.t2Label || 'Verbal'} Days`, value:selectedClass.verbalDays?.length ? fmtDays(selectedClass.verbalDays) : 'Not set', sub: selectedClass.verbalStartTime ? `${fmtTime12(selectedClass.verbalStartTime)}${selectedClass.verbalEndTime ? `–${fmtTime12(selectedClass.verbalEndTime)}` : ''}` : '', color:'#7c3aed' },
              { label:'Students', value:`${classStudents.length} / ${selectedClass.maxStudents || '∞'}`, color:'#059669' },
            ] : [
              { label:'Test Type', value:selectedClass.testType, color:cfg.color || '#374151' },
              { label:'Schedule', value:selectedClass.scheduleDays?.length ? fmtDays(selectedClass.scheduleDays) : 'Not set', color:'#374151' },
              { label:'Time', value:selectedClass.startTime ? `${fmtTime12(selectedClass.startTime)}${selectedClass.endTime ? `–${fmtTime12(selectedClass.endTime)}` : ''}` : 'Not set', color:'#374151' },
              { label:'Students', value:`${classStudents.length} / ${selectedClass.maxStudents || '∞'}`, color:'#059669' },
            ];
            return (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                {tiles.map(item => (
                  <div key={item.label} style={{ background:'#fff', borderRadius:10, padding:14, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', border:'1px solid rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>{item.label}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:item.color }}>{item.value}</div>
                    {item.sub && <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{item.sub}</div>}
                  </div>
                ))}
              </div>
            );
          })()}
          {/* ── Test Series Timeline ─────────────────────────────────── */}
          <div style={{ marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:'#0f172a', display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:18 }}>📋</span> Test Schedule
                  <span style={{ fontSize:11, fontWeight:600, color:'#94a3b8', fontWeight:400 }}>— mock tests & practice tests before the real exam</span>
                </div>
              </div>
              {canEdit && (
                <button onClick={() => setShowAddTestSeries(true)}
                  style={{ padding:'8px 16px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer', boxShadow:'0 3px 12px rgba(245,158,11,0.35)', display:'flex', alignItems:'center', gap:6 }}>
                  + Add Test
                </button>
              )}
            </div>

            {classTestSeries.length === 0 ? (
              <div style={{ background:'linear-gradient(135deg,#fffbeb,#fff7ed)', borderRadius:14, padding:'28px 24px', border:'1.5px dashed rgba(245,158,11,0.4)', textAlign:'center' }}>
                <div style={{ fontSize:36, marginBottom:8 }}>🗓️</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#92400e', marginBottom:4 }}>No tests scheduled yet</div>
                <div style={{ fontSize:12, color:'#b45309', lineHeight:1.5 }}>Schedule diagnostic tests, mock exams, and practice sessions to track preparation milestones</div>
              </div>
            ) : (
              <div style={{ background:'#fff', borderRadius:14, padding:'20px 20px 8px', boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
                {/* Stats row */}
                <div style={{ display:'flex', gap:12, marginBottom:18, flexWrap:'wrap' }}>
                  {Object.entries(SERIES_TYPES).map(([key, val]) => {
                    const count = classTestSeries.filter(ts => ts.type === key).length;
                    if (!count) return null;
                    return (
                      <div key={key} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:20, background:val.bg, border:`1px solid ${val.badge}` }}>
                        <span style={{ fontSize:13 }}>{val.icon}</span>
                        <span style={{ fontSize:11, fontWeight:700, color:val.color }}>{count} {val.label}</span>
                      </div>
                    );
                  })}
                  <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:20, background:'#f8fafc', border:'1px solid rgba(0,0,0,0.08)' }}>
                    <span style={{ fontSize:13 }}>📊</span>
                    <span style={{ fontSize:11, fontWeight:700, color:'#374151' }}>{classTestSeries.length} total events</span>
                  </div>
                </div>

                {/* Timeline */}
                <div style={{ position:'relative' }}>
                  <div style={{ position:'absolute', left:22, top:20, bottom:20, width:2, background:'linear-gradient(to bottom,#e2e8f0 0%,#c4b5fd 50%,#e2e8f0 100%)', borderRadius:1 }} />
                  <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                    {classTestSeries.map((ts) => {
                      const stCfg = SERIES_TYPES[ts.type] || SERIES_TYPES.mock;
                      const d = daysUntil(ts.date);
                      const isPast = d !== null && d < 0;
                      const isToday2 = d === 0;
                      const isSoon = d !== null && d > 0 && d <= 7;
                      const tsScore = scores.find(sc => sc.date === ts.date && sc.classId === selectedClassId);
                      return (
                        <div key={ts.id} style={{ display:'flex', gap:16, paddingBottom:16, alignItems:'flex-start' }}>
                          {/* Node */}
                          <div style={{ position:'relative', zIndex:1, flexShrink:0, width:46, display:'flex', justifyContent:'center', paddingTop:4 }}>
                            <div style={{
                              width:42, height:42, borderRadius:12,
                              background:isPast ? '#f1f5f9' : stCfg.bg,
                              border:`2px solid ${isPast ? '#cbd5e1' : stCfg.color}`,
                              display:'flex', alignItems:'center', justifyContent:'center', fontSize:18,
                              opacity:isPast ? 0.7 : 1,
                              boxShadow: isToday2 ? `0 0 0 4px ${stCfg.color}25` : !isPast ? `0 3px 8px ${stCfg.color}25` : 'none',
                            }}>
                              {isPast ? '✓' : stCfg.icon}
                            </div>
                          </div>
                          {/* Card */}
                          <div style={{
                            flex:1, borderRadius:12, padding:'12px 16px',
                            background:isToday2 ? stCfg.bg : isPast ? '#fafafa' : '#fff',
                            border:`1.5px solid ${isToday2 ? stCfg.color : isPast ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.08)'}`,
                            marginBottom:2,
                          }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                              <div style={{ flex:1 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap' }}>
                                  <span style={{ padding:'2px 8px', borderRadius:5, background:stCfg.badge, fontSize:10, fontWeight:700, color:stCfg.color }}>{stCfg.label}</span>
                                  {isToday2 && <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(239,68,68,0.12)', fontSize:10, fontWeight:700, color:'#dc2626' }}>TODAY</span>}
                                  {isSoon && <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(245,158,11,0.12)', fontSize:10, fontWeight:700, color:'#d97706' }}>THIS WEEK</span>}
                                  {isPast && <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(148,163,184,0.12)', fontSize:10, fontWeight:600, color:'#94a3b8' }}>COMPLETED</span>}
                                </div>
                                <div style={{ fontSize:14, fontWeight:700, color:isPast ? '#64748b' : '#0f172a' }}>{ts.name}</div>
                              </div>
                              <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:isPast ? '#94a3b8' : '#374151' }}>
                                  {new Date(ts.date + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                                </div>
                                <div style={{ fontSize:11, fontWeight:600, color:isPast ? '#94a3b8' : d === 0 ? '#dc2626' : d <= 7 ? '#d97706' : '#059669' }}>
                                  {isPast ? `${Math.abs(d)}d ago` : d === 0 ? 'Today' : `in ${d} days`}
                                </div>
                              </div>
                            </div>
                            {ts.startTime && (
                              <div style={{ fontSize:11, color:'#64748b', marginTop:5 }}>
                                ⏱ {fmtTime12(ts.startTime)}{ts.endTime ? `–${fmtTime12(ts.endTime)}` : ''}
                              </div>
                            )}
                            {ts.notes && (
                              <div style={{ fontSize:12, color:'#475569', marginTop:6, lineHeight:1.45, padding:'6px 10px', background:'rgba(0,0,0,0.03)', borderRadius:7 }}>{ts.notes}</div>
                            )}
                            {tsScore && (
                              <div style={{ marginTop:8, display:'inline-flex', alignItems:'center', gap:6, padding:'5px 10px', background:'rgba(5,150,105,0.09)', borderRadius:8, border:'1px solid rgba(5,150,105,0.2)' }}>
                                <span style={{ fontSize:11 }}>📊</span>
                                <span style={{ fontSize:12, fontWeight:700, color:'#059669' }}>Score recorded: {tsScore.total} / {TEST_CFG[tsScore.testType]?.maxTotal}</span>
                              </div>
                            )}
                            {canEdit && (
                              <div style={{ marginTop:8 }}>
                                <button onClick={() => deleteTestSeriesItem(ts.id)}
                                  style={{ padding:'3px 9px', borderRadius:6, border:'none', background:'rgba(239,68,68,0.08)', color:'#dc2626', fontSize:11, cursor:'pointer' }}>
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {enrollMode ? (
            <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:14 }}>Select Students to Enroll</div>
              <div style={{ maxHeight:440, overflowY:'auto', display:'grid', gap:8 }}>
                {allLeadsWithName.map(lead => {
                  const isEnrolled = classEnrollmentIds.has(lead.id);
                  return (
                    <div key={lead.id} onClick={() => toggleEnroll(lead.id)}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:10, cursor:'pointer', background:isEnrolled ? '#f5f3ff' : '#fafafa', border:`1.5px solid ${isEnrolled ? '#7c3aed' : 'rgba(0,0,0,0.07)'}`, transition:'all 0.15s' }}>
                      <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${isEnrolled ? '#7c3aed' : 'rgba(0,0,0,0.15)'}`, background:isEnrolled ? '#7c3aed' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#fff', flexShrink:0 }}>
                        {isEnrolled ? '✓' : ''}
                      </div>
                      <div style={{ fontWeight:600, fontSize:13, color:'#0f172a' }}>{getDisplayName(lead)}</div>
                      {lead.phone && <div style={{ fontSize:11, color:'#64748b', marginLeft:'auto' }}>{lead.phone}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:14 }}>Enrolled Students ({classStudents.length})</div>
              {classStudents.length === 0 ? (
                <div style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'36px 0' }}>No students enrolled. Use "+ Enroll" to add some.</div>
              ) : classStudents.map(student => {
                const studentEnr = enrollments.find(e => e.classId === selectedClassId && e.leadId === student.id);
                const sScores = scores.filter(s => s.studentId === student.id && s.testType === selectedClass.testType);
                const best = sScores.length ? sScores.reduce((a, b) => ((b.total || 0) > (a.total || 0) ? b : a), sScores[0]) : null;
                const d = daysUntil(studentEnr?.actualTestDate);
                const target = studentEnr?.targetScore ? parseFloat(studentEnr.targetScore) : null;
                const gap = target !== null && best?.total != null ? target - parseFloat(best.total) : null;
                const testCfg = TEST_CFG[selectedClass.testType] || {};
                const urgColor = d === null ? '#64748b' : d <= 0 ? '#059669' : d <= 14 ? '#dc2626' : d <= 30 ? '#d97706' : '#059669';
                return (
                  <div key={student.id} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'14px 0', borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
                    {/* Avatar */}
                    <div onClick={() => { setTab('students'); setSelectedStudentId(student.id); }} style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#7c3aed,#3b82f6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:800, color:'#fff', flexShrink:0, cursor:'pointer' }}>
                      {getDisplayName(student).charAt(0) || '?'}
                    </div>
                    {/* Info */}
                    <div style={{ flex:1, minWidth:0, cursor:'pointer' }} onClick={() => { setTab('students'); setSelectedStudentId(student.id); }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#0f172a' }}>{getDisplayName(student)}</div>
                      <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>{student.email || student.phone || ''}</div>
                      {/* Teacher chips */}
                      {(selectedClass?.teacher1Name || selectedClass?.teacher2Name) && (
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:4 }}>
                          {selectedClass.teacher1Name && (
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:8, background:'#eff6ff', color:'#1e40af', border:'1px solid #bfdbfe', fontWeight:600 }}>
                              📐 {selectedClass.teacher1Name}
                            </span>
                          )}
                          {selectedClass.teacher2Name && selectedClass.teacher2Name !== selectedClass.teacher1Name && (
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:8, background:'#f5f3ff', color:'#6d28d9', border:'1px solid #ede9fe', fontWeight:600 }}>
                              📖 {selectedClass.teacher2Name}
                            </span>
                          )}
                          {selectedClass.teacher2Name && selectedClass.teacher2Name === selectedClass.teacher1Name && (
                            <span style={{ fontSize:9, padding:'1px 6px', borderRadius:8, background:'#f0fdf4', color:'#166534', border:'1px solid #bbf7d0', fontWeight:600 }}>
                              👤 {selectedClass.teacher1Name}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Countdown + target + gap badges */}
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                        {studentEnr?.actualTestDate && (
                          <span style={{ padding:'2px 8px', borderRadius:5, background: d <= 14 && d >= 0 ? 'rgba(239,68,68,0.1)' : d <= 30 && d >= 0 ? 'rgba(245,158,11,0.1)' : 'rgba(5,150,105,0.1)', fontSize:11, fontWeight:700, color:urgColor }}>
                            🎯 {d === null ? '' : d <= 0 ? 'Test day passed' : `${d}d to test`} · {fmtShortDate(studentEnr.actualTestDate)}
                          </span>
                        )}
                        {target !== null && (
                          <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(124,58,237,0.08)', fontSize:11, fontWeight:700, color:'#7c3aed' }}>
                            Target: {target}/{testCfg.maxTotal}
                          </span>
                        )}
                        {gap !== null && gap <= 0 && (
                          <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(5,150,105,0.1)', fontSize:11, fontWeight:700, color:'#059669' }}>✓ Target reached</span>
                        )}
                        {gap !== null && gap > 0 && (
                          <span style={{ padding:'2px 8px', borderRadius:5, background:'rgba(245,158,11,0.1)', fontSize:11, fontWeight:600, color:'#d97706' }}>Gap: +{gap.toFixed(0)} pts needed</span>
                        )}
                      </div>
                    </div>
                    {/* Best score */}
                    {best && (
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:18, fontWeight:800, color:testCfg.color }}>{best.total}</div>
                        <div style={{ fontSize:10, color:'#94a3b8' }}>Best</div>
                      </div>
                    )}
                    {/* Edit enrollment */}
                    {canEdit && studentEnr && (
                      <button onClick={(e) => { e.stopPropagation(); setEditingEnrollmentData({ enrollment:studentEnr, student }); setShowEditEnrollment(true); }}
                        style={{ padding:'5px 10px', borderRadius:7, border:'1.5px solid rgba(0,0,0,0.1)', background:'#f8fafc', color:'#374151', fontSize:11, cursor:'pointer', flexShrink:0, fontWeight:600 }}>
                        ✏️ Info
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>Classes</div>
          {canEdit && <button onClick={() => { setEditingClass(null); setShowAddClass(true); }} style={{ padding:'9px 18px', borderRadius:9, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>+ New Class</button>}
        </div>
        {classes.length === 0 ? (
          <div style={{ textAlign:'center', padding:'80px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📚</div>
            <div style={{ fontSize:15, fontWeight:600 }}>No classes yet</div>
            <div style={{ fontSize:13, marginTop:4 }}>Create your first class to get started</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:16 }}>
            {classes.map(cls => {
              const color = classColorMap[cls.id] || '#7c3aed';
              const cfg = TEST_CFG[cls.testType] || {};
              const enCount = enrollments.filter(e => e.classId === cls.id).length;
              return (
                <div key={cls.id} onClick={() => setSelectedClassId(cls.id)}
                  style={{ background:'#fff', borderRadius:14, overflow:'hidden', boxShadow:'0 2px 12px rgba(0,0,0,0.07)', border:'1.5px solid rgba(0,0,0,0.06)', cursor:'pointer', transition:'transform 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'; }}>
                  <div style={{ height:5, background:color }} />
                  <div style={{ padding:18 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div style={{ fontSize:15, fontWeight:800, color:'#0f172a', flex:1 }}>{cls.name}</div>
                      <span style={{ padding:'2px 8px', borderRadius:6, background:cfg.badge, fontSize:11, fontWeight:700, color:cfg.color, marginLeft:8, flexShrink:0 }}>{cls.testType}</span>
                    </div>
                    {/* Teacher(s) */}
                    {(() => {
                      const tcfg = TEST_CFG[cls.testType] || {};
                      if (tcfg.dualTeacher) {
                        const t1 = cls.teacher1Name; const t2 = cls.teacher2Name;
                        const same = t1 && t2 && cls.teacher1Id === cls.teacher2Id;
                        return (
                          <div style={{ marginBottom:6 }}>
                            {t1 && <div style={{ fontSize:11, color:'#2563eb', fontWeight:600, marginBottom:2 }}>📘 {tcfg.t1Label}: <span style={{ color:'#374151', fontWeight:500 }}>{t1}</span></div>}
                            {t2 && !same && <div style={{ fontSize:11, color:'#7c3aed', fontWeight:600 }}>📗 {tcfg.t2Label}: <span style={{ color:'#374151', fontWeight:500 }}>{t2}</span></div>}
                            {same && <div style={{ fontSize:10, color:'#d97706', marginTop:1 }}>(same teacher for both)</div>}
                          </div>
                        );
                      }
                      const name = cls.teacher1Name || cls.teacherName;
                      return name ? <div style={{ fontSize:12, color:'#64748b', marginBottom:6 }}>👤 {name}</div> : null;
                    })()}
                    {/* Schedule */}
                    {cls.scheduleDays?.length > 0
                      ? <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>🕐 {fmtDays(cls.scheduleDays)}{cls.startTime ? ` · ${fmtTime12(cls.startTime)}${cls.endTime ? `–${fmtTime12(cls.endTime)}` : ''}` : ''}</div>
                      : cls.schedule ? <div style={{ fontSize:11, color:'#64748b', marginBottom:4 }}>🕐 {cls.schedule}</div> : null
                    }
                    {/* Status badge */}
                    {cls.status === 'closed' && <span style={{ display:'inline-block', padding:'1px 7px', borderRadius:5, background:'rgba(239,68,68,0.1)', fontSize:10, fontWeight:700, color:'#dc2626', marginBottom:4 }}>CLOSED</span>}
                    {cls.description && <div style={{ fontSize:12, color:'#94a3b8', lineHeight:1.4, marginBottom:8 }}>{cls.description}</div>}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10, paddingTop:10, borderTop:'1px solid rgba(0,0,0,0.05)' }}>
                      <div style={{ fontSize:12, color:'#374151' }}><strong>{enCount}</strong> / {cls.maxStudents || '∞'} students</div>
                      {canEdit && (
                        <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
                          <button onClick={(e) => { e.stopPropagation(); setEditingClass(cls); setShowAddClass(true); }} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:11 }}>Edit</button>
                          <button onClick={(e) => { e.stopPropagation(); toggleClassStatus(cls); }} style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${cls.status === 'closed' ? '#059669' : '#f59e0b'}`, background:cls.status === 'closed' ? 'rgba(5,150,105,0.08)' : 'rgba(245,158,11,0.08)', cursor:'pointer', fontSize:11, color:cls.status === 'closed' ? '#059669' : '#d97706' }}>
                            {cls.status === 'closed' ? 'Reopen' : 'Close'}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteClass(cls.id); }} style={{ padding:'4px 10px', borderRadius:6, border:'none', background:'rgba(239,68,68,0.1)', cursor:'pointer', fontSize:11, color:'#dc2626' }}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Schedule ──────────────────────────────────────────────────────
  const renderSchedule = () => {
    const todayStr = today();
    return (
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>Schedule</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:13 }}>← Prev</button>
            <button onClick={() => setWeekOffset(0)} style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:12 }}>Today</button>
            <button onClick={() => setWeekOffset(w => w + 1)} style={{ padding:'7px 12px', borderRadius:8, border:'1.5px solid rgba(0,0,0,0.1)', background:'#fff', cursor:'pointer', fontSize:13 }}>Next →</button>
            {canEdit && <button onClick={() => setShowAddSession(true)} style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer', marginLeft:8 }}>+ Session</button>}
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8, marginBottom:28 }}>
          {weekDays.map((day, di) => {
            const dateStr = day.toISOString().split('T')[0];
            const isToday = dateStr === todayStr;
            const daySessions = weekSessions.filter(s => s.date === dateStr);
            return (
              <div key={di} style={{ minHeight:120 }}>
                <div style={{ textAlign:'center', marginBottom:8 }}>
                  <div style={{ fontSize:10, color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>{DAY_LABELS[day.getDay()]}</div>
                  <div style={{ width:30, height:30, borderRadius:'50%', background:isToday ? '#7c3aed' : 'transparent', color:isToday ? '#fff' : '#374151', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:isToday ? 800 : 500, margin:'4px auto 0' }}>
                    {day.getDate()}
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  {daySessions.map(sess => {
                    const baseColor = classColorMap[sess.classId] || '#7c3aed';
                    const subjectColor = sess.sessionSubject === 'math' ? '#2563eb' : sess.sessionSubject === 'verbal' ? '#7c3aed' : baseColor;
                    const cfg = TEST_CFG[sess.testType] || {};
                    return (
                      <div key={sess.id} style={{ background:'#fff', border:`1.5px solid ${subjectColor}22`, borderLeft:`4px solid ${subjectColor}`, borderRadius:7, padding:'6px 8px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:subjectColor, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sess.className}</div>
                        {sess.sessionSubject && <div style={{ fontSize:9, fontWeight:700, color:subjectColor, marginTop:1, textTransform:'uppercase', letterSpacing:'0.04em' }}>{sess.topic}</div>}
                        {sess.startTime && <div style={{ fontSize:9, color:'#94a3b8', marginTop:1 }}>{fmtTime12(sess.startTime)}{sess.endTime ? `–${fmtTime12(sess.endTime)}` : ''}</div>}
                        {sess.teacherName && <div style={{ fontSize:9, color:'#64748b', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>👤 {sess.teacherName}</div>}
                        {!sess.sessionSubject && sess.topic && <div style={{ fontSize:9, color:'#64748b', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sess.topic}</div>}
                        {sess.testType && <span style={{ fontSize:9, padding:'1px 4px', borderRadius:3, background:cfg.badge || '#ede9fe', color:cfg.color || subjectColor, fontWeight:700, marginTop:2, display:'inline-block' }}>{sess.testType}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Upcoming list */}
        <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:14 }}>All Upcoming Sessions</div>
          {sessions.filter(s => s.date >= todayStr).length === 0 ? (
            <div style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'24px 0' }}>No upcoming sessions scheduled</div>
          ) : sessions.filter(s => s.date >= todayStr).slice(0, 20).map(sess => {
            const color = classColorMap[sess.classId] || '#7c3aed';
            const cfg = TEST_CFG[sess.testType] || {};
            return (
              <div key={sess.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ width:4, height:36, borderRadius:2, background:color, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{sess.className}</div>
                  <div style={{ fontSize:11, color:'#64748b' }}>{sess.date}{sess.startTime ? ` · ${sess.startTime}` : ''}{sess.endTime ? `–${sess.endTime}` : ''}</div>
                  {sess.topic && <div style={{ fontSize:11, color:'#94a3b8' }}>{sess.topic}</div>}
                </div>
                {sess.testType && <span style={{ padding:'2px 8px', borderRadius:6, background:cfg.badge, fontSize:11, fontWeight:700, color:cfg.color }}>{sess.testType}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render: Scores ────────────────────────────────────────────────────────
  const renderScores = () => (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>Scores</div>
        {canEdit && <button onClick={() => setShowAddScore(true)} style={{ padding:'9px 18px', borderRadius:9, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>+ Record Score</button>}
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
        {['all', ...TEST_TYPES].map(t => {
          const cfg = TEST_CFG[t];
          const active = scoreFilter === t;
          return (
            <button key={t} onClick={() => setScoreFilter(t)}
              style={{ padding:'6px 14px', borderRadius:8, border:`1.5px solid ${active ? (cfg?.color || '#7c3aed') : 'rgba(0,0,0,0.1)'}`, background:active ? (cfg?.badge || '#ede9fe') : '#fff', color:active ? (cfg?.color || '#7c3aed') : '#374151', fontWeight:600, fontSize:12, cursor:'pointer' }}>
              {t === 'all' ? 'All Tests' : t}
            </button>
          );
        })}
      </div>
      <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
        {filteredScores.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📊</div>
            <div style={{ fontSize:14, fontWeight:600 }}>No scores yet</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>{['Student', 'Test', 'Score', 'Sections', 'Date', 'Notes'].map(h => <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'#64748b', fontWeight:600, borderBottom:'1px solid rgba(0,0,0,0.08)', whiteSpace:'nowrap' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filteredScores.map(sc => {
                  const cfg = TEST_CFG[sc.testType] || {};
                  const total = sc.total ?? calcTotal(sc.sections, sc.testType);
                  const pct = getScorePercent(total, sc.testType);
                  const perfColor = pct >= 80 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
                  return (
                    <tr key={sc.id} style={{ borderBottom:'1px solid rgba(0,0,0,0.04)' }}>
                      <td style={{ padding:'10px 12px' }}>
                        <button onClick={() => { setSelectedStudentId(sc.studentId); setTab('students'); }} style={{ background:'none', border:'none', cursor:'pointer', fontWeight:600, color:'#7c3aed', fontSize:13, padding:0, textDecoration:'underline' }}>
                          {sc.studentName || '—'}
                        </button>
                      </td>
                      <td style={{ padding:'10px 12px' }}><span style={{ padding:'3px 8px', borderRadius:6, background:cfg.badge, fontSize:11, fontWeight:700, color:cfg.color }}>{sc.testType}</span></td>
                      <td style={{ padding:'10px 12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontWeight:800, fontSize:14, color:cfg.color }}>{total ?? '–'}</span>
                          <span style={{ fontSize:11, color:'#94a3b8' }}>/{cfg.maxTotal}</span>
                          <span style={{ fontSize:11, fontWeight:600, color:perfColor }}>({pct}%)</span>
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px' }}>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {(cfg.sections || []).map(s => <span key={s} style={{ fontSize:11, color:'#64748b', whiteSpace:'nowrap' }}>{s}: <strong>{sc.sections?.[s] ?? '–'}</strong></span>)}
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px', color:'#64748b', whiteSpace:'nowrap' }}>{typeof sc.date === 'string' ? sc.date : fmtDate(sc.date)}</td>
                      <td style={{ padding:'10px 12px', color:'#94a3b8', maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{sc.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Render: Notifications ─────────────────────────────────────────────────
  const renderNotifications = () => (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#0f172a' }}>Parent Notifications</div>
        {canEdit && <button onClick={() => setShowNotification(true)} style={{ padding:'9px 18px', borderRadius:9, border:'none', background:'#7c3aed', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>📨 Send Notification</button>}
      </div>
      <div style={{ background:'#fff', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.06)' }}>
        {notifications.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 20px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📬</div>
            <div style={{ fontSize:14, fontWeight:600 }}>No notifications sent yet</div>
            <div style={{ fontSize:12, marginTop:4 }}>Keep parents informed about their child's progress</div>
          </div>
        ) : notifications.map(n => {
          const ch = n.channel === 'whatsapp' ? '💬' : n.channel === 'email' ? '📧' : '📱';
          return (
            <div key={n.id} style={{ padding:'14px 0', borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:16 }}>{ch}</span>
                  <div style={{ fontWeight:700, color:'#0f172a', fontSize:13 }}>{n.subject || 'No Subject'}</div>
                </div>
                <div style={{ fontSize:11, color:'#94a3b8', whiteSpace:'nowrap', marginLeft:12 }}>{fmtDate(n.sentAt)}</div>
              </div>
              <div style={{ fontSize:12, color:'#475569', lineHeight:1.55, marginBottom:6 }}>{n.message}</div>
              <div style={{ display:'flex', gap:10, fontSize:11, color:'#94a3b8' }}>
                <span>By {n.sentBy || '—'}</span>
                <span>·</span>
                <span>{n.recipientCount || 0} recipient{(n.recipientCount || 0) !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span style={{ textTransform:'capitalize' }}>{n.channel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Main Render ───────────────────────────────────────────────────────────
  const tabContent = { dashboard:renderDashboard(), students:renderStudents(), classes:renderClasses(), schedule:renderSchedule(), scores:renderScores(), notifications:renderNotifications() };

  return (
    <div style={{ height:'100%', overflow:'auto', background:'var(--bg-primary)', fontFamily:"'Inter', sans-serif" }}>
      {/* Top bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid rgba(0,0,0,0.08)', padding:'0 24px', position:'sticky', top:0, zIndex:50 }}>
        <div style={{ paddingTop:16 }}>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#0f172a', margin:0 }}>Test Prep</h1>
          <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>SAT · ACT · GRE · GMAT · TOEFL · IELTS · PTE · DET</div>
        </div>
        <div style={{ display:'flex', gap:0, marginTop:14 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setSelectedClassId(null); setSelectedStudentId(null); setEnrollMode(false); }}
              style={{ padding:'10px 18px', border:'none', borderBottom:`3px solid ${tab === t.id ? '#7c3aed' : 'transparent'}`, background:'transparent', color:tab === t.id ? '#7c3aed' : '#64748b', fontWeight:tab === t.id ? 700 : 500, fontSize:13, cursor:'pointer', fontFamily:'inherit', transition:'color 0.15s' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tabContent[tab]}

      {/* Modals */}
      <AddClassModal
        open={showAddClass}
        onClose={() => { setShowAddClass(false); setEditingClass(null); }}
        onSave={saveClass}
        teachers={teachers}
        editData={editingClass}
      />
      <AddSessionModal open={showAddSession} onClose={() => setShowAddSession(false)} onSave={saveSession} classes={classes} />
      <AddScoreModal
        open={showAddScore}
        onClose={() => setShowAddScore(false)}
        onSave={saveScore}
        students={allStudents.length ? allStudents : allLeadsWithName}
        classes={classes}
      />
      <NotificationModal open={showNotification} onClose={() => setShowNotification(false)} onSend={sendNotification} students={allStudents} />
      <AddTestSeriesModal
        open={showAddTestSeries}
        onClose={() => setShowAddTestSeries(false)}
        onSave={saveTestSeriesItem}
        classId={selectedClassId}
        classes={classes}
      />
      <EditEnrollmentModal
        open={showEditEnrollment}
        onClose={() => { setShowEditEnrollment(false); setEditingEnrollmentData(null); }}
        onSave={updateEnrollment}
        enrollment={editingEnrollmentData?.enrollment}
        student={editingEnrollmentData?.student}
        testType={selectedClass?.testType || editingEnrollmentData?.enrollment?.testType}
      />
    </div>
  );
}
